/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import Info from "./info";
import { FixedAssetsDepreciationService } from "services/fixed-assets-depreciation-schedule";
import { getPrintTemplate } from "./components/printTemplate";

const initialValues = {
  unit: "",
  department: "",
  documentNumber: "",
  month: "",
  year: "",
  signingDate: "",
  preparerName: "",
  accountantName: "",
  items: [],
  total: null,
};

const createItem = (orderNumber, itemName, rateOrPeriod) => ({
  orderNumber,
  itemName,
  rateOrPeriod,
  fixedAssetCost: "",
  depreciationAmount: "",
  workshop1: "",
  workshop2: "",
  workshop3: "",
  workshop4: "",
  tk623ConstructionMachine: "",
  tk641SellingExpense: "",
  tk642AdministrativeExpense: "",
  tk241ConstructionInProgress: "",
  tk242DeferredExpense: "",
  tk335AccruedExpense: "",
  tkOther: "",
});

const defaultItems = [
  createItem("1", "I. Số khấu hao trích tháng trước", ""),
  createItem("2", "II. Số KH TSCĐ tăng trong tháng", ""),
  createItem("3", "III. Số KH TSCĐ giảm trong tháng", ""),
];

function FixedAssetsDepreciationScheduleDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containEdit = location.pathname?.includes("/edit");
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [getData, setGetData] = useState({});
  const [data, setData] = useState({});

  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await FixedAssetsDepreciationService.getById(id);
        const apiData = response.data ? response.data : response;

        const mappedItems = (apiData.items || []).map((item) => ({
          orderNumber: item.orderNumber || "",
          itemName: item.itemName || "",
          rateOrPeriod: item.rateOrPeriod || "",
          fixedAssetCost: item.entireEnterprise?.fixedAssetCost || "",
          depreciationAmount: item.entireEnterprise?.depreciationAmount || "",
          workshop1: item.accountAllocation?.tk627ProductionOverhead?.workshop1 || "",
          workshop2: item.accountAllocation?.tk627ProductionOverhead?.workshop2 || "",
          workshop3: item.accountAllocation?.tk627ProductionOverhead?.workshop3 || "",
          workshop4: item.accountAllocation?.tk627ProductionOverhead?.workshop4 || "",
          tk623ConstructionMachine: item.accountAllocation?.tk623ConstructionMachine || "",
          tk641SellingExpense: item.accountAllocation?.tk641SellingExpense || "",
          tk642AdministrativeExpense: item.accountAllocation?.tk642AdministrativeExpense || "",
          tk241ConstructionInProgress: item.accountAllocation?.tk241ConstructionInProgress || "",
          tk242DeferredExpense: item.accountAllocation?.tk242DeferredExpense || "",
          tk335AccruedExpense: item.accountAllocation?.tk335AccruedExpense || "",
          tkOther: item.accountAllocation?.tkOther || "",
        }));

        const mappedTotal = apiData.total
          ? {
              fixedAssetCost: apiData.total.entireEnterprise?.fixedAssetCost || "",
              depreciationAmount: apiData.total.entireEnterprise?.depreciationAmount || "",
              workshop1: apiData.total.accountAllocation?.tk627ProductionOverhead?.workshop1 || "",
              workshop2: apiData.total.accountAllocation?.tk627ProductionOverhead?.workshop2 || "",
              workshop3: apiData.total.accountAllocation?.tk627ProductionOverhead?.workshop3 || "",
              workshop4: apiData.total.accountAllocation?.tk627ProductionOverhead?.workshop4 || "",
              tk623ConstructionMachine:
                apiData.total.accountAllocation?.tk623ConstructionMachine || "",
              tk641SellingExpense: apiData.total.accountAllocation?.tk641SellingExpense || "",
              tk642AdministrativeExpense:
                apiData.total.accountAllocation?.tk642AdministrativeExpense || "",
              tk241ConstructionInProgress:
                apiData.total.accountAllocation?.tk241ConstructionInProgress || "",
              tk242DeferredExpense: apiData.total.accountAllocation?.tk242DeferredExpense || "",
              tk335AccruedExpense: apiData.total.accountAllocation?.tk335AccruedExpense || "",
              tkOther: apiData.total.accountAllocation?.tkOther || "",
            }
          : null;

        const loadedData = {
          ...initialValues,
          unit: apiData.unit || "",
          department: apiData.department || "",
          documentNumber: apiData.documentNumber || "",
          month: apiData.month || "",
          year: apiData.year || "",
          signingDate: apiData.signingDate || "",
          preparerName: apiData.preparerName || "",
          accountantName: apiData.accountantName || "",
          items: mappedItems.length > 0 ? mappedItems : defaultItems,
          total: mappedTotal,
        };

        setFormInitials(loadedData);
        setGetData(apiData);
      } catch (error) {
        console.error("Lỗi getById", error);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const onChangeValue = useCallback((type, value) => {
    setData((prev) => ({ ...prev, [type]: value }));
  }, []);

  const handleBack = () => navigation(-1);

  const submitForm = async (values) => {
    try {
      const num = (val) => (val && !isNaN(val) ? Number(val) : 0);

      // Get values from data state or form values
      const unit = data.unit !== undefined ? data.unit : values.unit;
      const department = data.department !== undefined ? data.department : values.department;
      const documentNumber =
        data.documentNumber !== undefined ? data.documentNumber : values.documentNumber;
      const month = data.month !== undefined ? data.month : values.month;
      const year = data.year !== undefined ? data.year : values.year;
      const signingDate = data.signingDate !== undefined ? data.signingDate : values.signingDate;
      const preparerName =
        data.preparerName !== undefined ? data.preparerName : values.preparerName;
      const accountantName =
        data.accountantName !== undefined ? data.accountantName : values.accountantName;
      const items = data.items !== undefined ? data.items : values.items || [];
      const total = data.total !== undefined ? data.total : values.total;

      const buildItems = (items) => {
        return items.map((item) => ({
          orderNumber: item.orderNumber || "",
          itemName: item.itemName || "",
          rateOrPeriod: item.rateOrPeriod || "",
          entireEnterprise: {
            fixedAssetCost: num(item.fixedAssetCost),
            depreciationAmount: num(item.depreciationAmount),
          },
          accountAllocation: {
            tk627ProductionOverhead: {
              workshop1: num(item.workshop1),
              workshop2: num(item.workshop2),
              workshop3: num(item.workshop3),
              workshop4: num(item.workshop4),
            },
            tk623ConstructionMachine: num(item.tk623ConstructionMachine),
            tk641SellingExpense: num(item.tk641SellingExpense),
            tk642AdministrativeExpense: num(item.tk642AdministrativeExpense),
            tk241ConstructionInProgress: num(item.tk241ConstructionInProgress),
            tk242DeferredExpense: num(item.tk242DeferredExpense),
            tk335AccruedExpense: num(item.tk335AccruedExpense),
            tkOther: item.tkOther || {},
          },
        }));
      };

      const buildTotal = (totalData) => {
        if (!totalData) {
          // Build default total from items
          const defaultTotal = {
            fixedAssetCost: 0,
            depreciationAmount: 0,
            workshop1: 0,
            workshop2: 0,
            workshop3: 0,
            workshop4: 0,
            tk623ConstructionMachine: 0,
            tk641SellingExpense: 0,
            tk642AdministrativeExpense: 0,
            tk241ConstructionInProgress: 0,
            tk242DeferredExpense: 0,
            tk335AccruedExpense: 0,
          };

          // Calculate totals from items (row I + row II - row III)
          items.forEach((item, idx) => {
            if (idx === 0) {
              // Row I - add
              defaultTotal.fixedAssetCost += num(item.fixedAssetCost);
              defaultTotal.depreciationAmount += num(item.depreciationAmount);
              defaultTotal.workshop1 += num(item.workshop1);
              defaultTotal.workshop2 += num(item.workshop2);
              defaultTotal.workshop3 += num(item.workshop3);
              defaultTotal.workshop4 += num(item.workshop4);
              defaultTotal.tk623ConstructionMachine += num(item.tk623ConstructionMachine);
              defaultTotal.tk641SellingExpense += num(item.tk641SellingExpense);
              defaultTotal.tk642AdministrativeExpense += num(item.tk642AdministrativeExpense);
              defaultTotal.tk241ConstructionInProgress += num(item.tk241ConstructionInProgress);
              defaultTotal.tk242DeferredExpense += num(item.tk242DeferredExpense);
              defaultTotal.tk335AccruedExpense += num(item.tk335AccruedExpense);
            } else if (idx === 1) {
              // Row II - add
              defaultTotal.fixedAssetCost += num(item.fixedAssetCost);
              defaultTotal.depreciationAmount += num(item.depreciationAmount);
              defaultTotal.workshop1 += num(item.workshop1);
              defaultTotal.workshop2 += num(item.workshop2);
              defaultTotal.workshop3 += num(item.workshop3);
              defaultTotal.workshop4 += num(item.workshop4);
              defaultTotal.tk623ConstructionMachine += num(item.tk623ConstructionMachine);
              defaultTotal.tk641SellingExpense += num(item.tk641SellingExpense);
              defaultTotal.tk642AdministrativeExpense += num(item.tk642AdministrativeExpense);
              defaultTotal.tk241ConstructionInProgress += num(item.tk241ConstructionInProgress);
              defaultTotal.tk242DeferredExpense += num(item.tk242DeferredExpense);
              defaultTotal.tk335AccruedExpense += num(item.tk335AccruedExpense);
            } else if (idx === 2) {
              // Row III - subtract
              defaultTotal.fixedAssetCost -= num(item.fixedAssetCost);
              defaultTotal.depreciationAmount -= num(item.depreciationAmount);
              defaultTotal.workshop1 -= num(item.workshop1);
              defaultTotal.workshop2 -= num(item.workshop2);
              defaultTotal.workshop3 -= num(item.workshop3);
              defaultTotal.workshop4 -= num(item.workshop4);
              defaultTotal.tk623ConstructionMachine -= num(item.tk623ConstructionMachine);
              defaultTotal.tk641SellingExpense -= num(item.tk641SellingExpense);
              defaultTotal.tk642AdministrativeExpense -= num(item.tk642AdministrativeExpense);
              defaultTotal.tk241ConstructionInProgress -= num(item.tk241ConstructionInProgress);
              defaultTotal.tk242DeferredExpense -= num(item.tk242DeferredExpense);
              defaultTotal.tk335AccruedExpense -= num(item.tk335AccruedExpense);
            }
          });

          return {
            entireEnterprise: {
              fixedAssetCost: defaultTotal.fixedAssetCost,
              depreciationAmount: defaultTotal.depreciationAmount,
            },
            accountAllocation: {
              tk627ProductionOverhead: {
                workshop1: defaultTotal.workshop1,
                workshop2: defaultTotal.workshop2,
                workshop3: defaultTotal.workshop3,
                workshop4: defaultTotal.workshop4,
              },
              tk623ConstructionMachine: defaultTotal.tk623ConstructionMachine,
              tk641SellingExpense: defaultTotal.tk641SellingExpense,
              tk642AdministrativeExpense: defaultTotal.tk642AdministrativeExpense,
              tk241ConstructionInProgress: defaultTotal.tk241ConstructionInProgress,
              tk242DeferredExpense: defaultTotal.tk242DeferredExpense,
              tk335AccruedExpense: defaultTotal.tk335AccruedExpense,
              tkOther: {},
            },
          };
        }

        return {
          entireEnterprise: {
            fixedAssetCost: num(totalData.fixedAssetCost),
            depreciationAmount: num(totalData.depreciationAmount),
          },
          accountAllocation: {
            tk627ProductionOverhead: {
              workshop1: num(totalData.workshop1),
              workshop2: num(totalData.workshop2),
              workshop3: num(totalData.workshop3),
              workshop4: num(totalData.workshop4),
            },
            tk623ConstructionMachine: num(totalData.tk623ConstructionMachine),
            tk641SellingExpense: num(totalData.tk641SellingExpense),
            tk642AdministrativeExpense: num(totalData.tk642AdministrativeExpense),
            tk241ConstructionInProgress: num(totalData.tk241ConstructionInProgress),
            tk242DeferredExpense: num(totalData.tk242DeferredExpense),
            tk335AccruedExpense: num(totalData.tk335AccruedExpense),
            tkOther: totalData.tkOther || {},
          },
        };
      };

      const payload = {
        unit: unit || "Unknown Unit",
        department: department || "Unknown Department",
        month: num(month),
        year: num(year),
        documentNumber:
          documentNumber || `DA/${new Date().getFullYear()}/${String(num(month)).padStart(2, "0")}`,
        signingDate: signingDate || new Date().toISOString(),
        preparerName: preparerName || "Người lập bảng",
        accountantName: accountantName || "Kế toán trưởng",
        items: buildItems(items),
        total: buildTotal(total),
      };

      console.log("Submit payload:", JSON.stringify(payload, null, 2));

      if (!id) {
        await FixedAssetsDepreciationService.create(payload);
        toast.success("Tạo bảng khấu hao TSCĐ thành công!");
      } else {
        await FixedAssetsDepreciationService.update(id, payload);
        toast.success("Cập nhật bảng khấu hao TSCĐ thành công!");
      }
    } catch (error) {
      console.error("Lỗi khi gọi API lưu:", error);
      toast.error(error?.response?.data?.message || "Lưu thất bại!");
      throw error;
    }
  };

  const handlePrintDirect = (values) => {
    const today = new Date();
    const finalUnit = data.unit !== undefined ? data.unit : values.unit;
    const finalDepartment = data.department !== undefined ? data.department : values.department;
    const finalDocNumber =
      data.documentNumber !== undefined ? data.documentNumber : values.documentNumber;
    const finalItems = data.items !== undefined ? data.items : values.items || defaultItems;
    const finalTotal = data.total !== undefined ? data.total : values.total;
    const finalMonth = data.month !== undefined ? data.month : values.month || today.getMonth() + 1;
    const finalYear = data.year !== undefined ? data.year : values.year || today.getFullYear();

    const headerInfo = {
      unit: finalUnit,
      department: finalDepartment,
      docNumber: finalDocNumber,
      month: finalMonth,
      year: finalYear,
      day: values.signingDate ? new Date(values.signingDate).getDate() : today.getDate(),
    };

    const rows = finalItems.map((item) => ({
      stt: item.orderNumber,
      chiTieu: item.itemName,
      tyLe: item.rateOrPeriod,
      nguyenGia: item.fixedAssetCost,
      soKhauHao: item.depreciationAmount,
      tk627_4: item.workshop1,
      tk627_5: item.workshop2,
      tk627_6: item.workshop3,
      tk627_7: item.workshop4,
      tk623: item.tk623ConstructionMachine,
      tk641: item.tk641SellingExpense,
      tk642: item.tk642AdministrativeExpense,
      tk241: item.tk241ConstructionInProgress,
      tk242: item.tk242DeferredExpense,
      tk335: item.tk335AccruedExpense,
      tkKhac: item.tkOther,
    }));

    const totals = finalTotal
      ? {
          nguyenGia: finalTotal.fixedAssetCost,
          soKhauHao: finalTotal.depreciationAmount,
          tk627_4: finalTotal.workshop1,
          tk627_5: finalTotal.workshop2,
          tk627_6: finalTotal.workshop3,
          tk627_7: finalTotal.workshop4,
          tk623: finalTotal.tk623ConstructionMachine,
          tk641: finalTotal.tk641SellingExpense,
          tk642: finalTotal.tk642AdministrativeExpense,
          tk241: finalTotal.tk241ConstructionInProgress,
          tk242: finalTotal.tk242DeferredExpense,
          tk335: finalTotal.tk335AccruedExpense,
          tkKhac: finalTotal.tkOther,
        }
      : {};

    const htmlContent = getPrintTemplate({
      headerInfo,
      rows,
      totals,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleSubmit = async (values, actions) => {
    try {
      await submitForm(values);
    } catch (error) {
      console.error("Lỗi khi gọi API lưu:", error);
    } finally {
      if (actions) {
        actions.setTouched({});
        actions.setSubmitting(false);
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center" sx={{ height: "100%" }}>
          <Grid item xs={12} lg={12}>
            <Formik initialValues={formInitials} enableReinitialize={true} onSubmit={handleSubmit}>
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <SoftBox>
                        <Info
                          formData={{
                            values,
                            touched,
                            errors,
                            getData,
                            onChangeValue,
                            setFieldValue,
                          }}
                        />
                        <SoftBox mt={10} width="100%" display="flex" justifyContent="space-between">
                          <SoftButton variant="gradient" color="light" onClick={handleBack}>
                            Trở về
                          </SoftButton>

                          {containView ? (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                variant="gradient"
                                color="success"
                                onClick={() => handlePrintDirect(values)}
                                type="button"
                              >
                                In Bảng Khấu Hao
                              </SoftButton>
                            </SoftBox>
                          ) : (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                disabled={isSubmitting}
                                type="submit"
                                variant="gradient"
                                color="dark"
                              >
                                {id ? "Lưu" : "Tạo"}
                              </SoftButton>
                            </SoftBox>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default FixedAssetsDepreciationScheduleDetail;
